import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { APP_VERSION } from '@/lib/version';

const updates = [
  {
    version: '1.9.2',
    date: '2026-01-23',
    title: 'Outside Wholesale integration for OutQuotes',
    changes: [
      'Added support for Outside Wholesale integration for OutQuotes',
    ],
  },
  {
    version: '1.9.1',
    date: '2025-09-13',
    title: 'Apollo integration for OutQuotes',
    changes: [
      'Added support for Apollo integration for OutQuotes',
    ],
  },
  {
    version: '1.9.0',
    date: '2025-07-26',
    title: 'UI Modernization & OQ Shipping Integration',
    changes: [
      'Added caching layer to prevent excessive NetSuite requests',
      'Added OutQuotes shipping status integration',
      'Improved loading speeds with performance optimizations',
      'Styling modernization with gradients, glass effects, and animations',
      'Removed legacy Mirage connector completely from backend services',
    ],
  },
  {
    version: '1.8.0',
    date: '2025-06-16',
    title: 'Calendar View and Navigation Enhancements',
    changes: [
      'Added new Calendar view alongside existing Kanban view in Overview tab',
      'Renamed "Kanban" tab to "Overview" with subtabs for Kanban and Calendar views',
      'Implemented drag-and-drop scheduling system for production planning',
      'Added weekly view showing current week (Monday-Friday) with detailed item cards',
      'Created database persistence for schedule data with real-time sync across users',
      'Added completion status display with crossed-out styling for finished items',
    ],
  },
  {
    version: '1.7.4',
    date: '2025-04-28',
    title: 'Visiblity of partially fulfilled items',
    changes: [
      'Added support for partially fulfilled sales orders from NetSuite',
    ],
  },
  {
    version: '1.7.3',
    date: '2025-04-24',
    title: 'Awning Configurator Update',
    changes: [
      'Awning configurators no longer show in the UI',
    ],
  },
  {
    version: '1.7.2',
    date: '2025-04-17',
    title: 'Buildout Configurator Update',
    changes: [
      'Changed buildout configurators to show metal and final assembly instead of kitting and metal',
    ],
  },
  {
    version: '1.7.1',
    date: '2025-04-10',
    title: 'Bug Fixes',
    changes: [
      "Fixed issue where SO's with non-configurator items were not showing in the UI when complete (NetSuite Connector)",
    ],
  },
  {
    version: '1.7.0',
    date: '2025-04-07',
    title: 'Buildout Integration',
    changes: [
      'Added Buildout integration from NetSuite',
      'Added visual differentiation between Screen Units and Buildouts',
      'Buildout items only show kitting and metal phases in production workflow',
      'Limited API requests to NetSuite (max 1 per 5 seconds)',
    ],
  },
  {
    version: '1.6.1',
    date: '2025-03-31',
    title: 'Kanban View Adjustments',
    changes: [
      'Adjusted view of Kanban UI view',
      'Fixed issue with Mark Complete button not working',
    ],
  },
  {
    version: '1.6.0',
    date: '2025-03-31',
    title: 'UI and Functionality Enhancements',
    changes: [
      'Added Non-Work Order items to appear in the tables',
      'Added NetSuite view buttons for sales orders and work orders in a new tab',
      'Enhanced visibility of NetSuite Table UI with visual grouping and clearer item separation',
      'Optimized UI display with conditional rendering based on current tab',
      'Simplified status indicators to reduce visual clutter on station-specific views',
    ],
  },
  {
    version: '1.5.0',
    date: '2025-03-12',
    title: 'Location-based access control',
    changes: [
      'Restricted "All Orders" tab to managers and admins only',
      'Added location-specific roles for access control (admins have access to all locations)',
      'Implemented permission checks for location access',
      'Added "All Locations" role for global access',
      'Improved location-based filtering for better security',
    ],
  },
  {
    version: '1.4.5',
    date: '2025-03-06',
    title: 'Templar OutQuotes integration bug fixes',
    changes: [
      'Fixed infinite OutQuotes login in backend',
      'Fixed validity of order status updates',
    ],
  },
  {
    version: '1.4.4',
    date: '2025-02-24',
    title: 'Templar OutQuotes integration',
    changes: ['Added Templar OutQuotes status integration', 'Added login on startup'],
  },
  {
    version: '1.4.3',
    date: '2025-02-17',
    title: 'Bug fixes',
    changes: [
      'Fixed to check all items in a group before considering a group done',
      'Deployment bug fixes',
    ],
  },
  {
    version: '1.4.2',
    date: '2025-02-17',
    title: 'Change log',
    changes: ['Added change log/timeline'],
  },
  {
    version: '1.4.1',
    date: '2024-02-17',
    title: 'Kanban Permission',
    changes: ['Added dedicated Kanban view permission'],
  },
  {
    version: '1.4.0',
    date: '2024-02-17',
    title: 'Kanban View Introduction',
    changes: [
      'Added Kanban view per location',
      'Real-time location status updates',
      'Location management',
    ],
  },
  {
    version: '1.3.0',
    date: '2024-01-06',
    title: 'Password Recovery',
    changes: [
      'Implemented forgot password feature',
      'Added email-based password reset',
      'Enhanced password security',
    ],
  },
  {
    version: '1.2.3',
    date: '2024-12-19',
    title: 'Workflow Optimization',
    changes: [
      'Changed workflow restrictions to [Kitting] -> [Fabric | Metal] -> [Assembly]',
      'Split fabric and metal dependencies',
      'Enhanced production workflow',
    ],
  },
  {
    version: '1.2.2',
    date: '2024-12-17',
    title: 'Facility Management',
    changes: [
      'Hardcoded WA & TX facilities within dropdown menu',
      'Added Grand Prairie and Auburn as location options',
    ],
  },
  {
    version: '1.2.1',
    date: '2024-12-10',
    title: 'Bug Fixes',
    changes: [
      'Fixed order not found error',
      'Fixed sorting by date string vs object',
      'General bug fixes and improvements',
    ],
  },
  {
    version: '1.2.0',
    date: '2024-12-10',
    title: 'OutQuotes Integration',
    changes: [
      'Added OutQuotes status integration',
      'Implemented automatic status updates',
      'Enhanced production tracking',
    ],
  },
  {
    version: '1.1.7',
    date: '2024-12-10',
    title: 'Authentication Fix',
    changes: ['Fixed logout on token expire', 'Improved session handling'],
  },
  {
    version: '1.1.6',
    date: '2024-12-09',
    title: 'Session Management',
    changes: ['Added logout on token expire', 'Enhanced authentication security'],
  },
  {
    version: '1.1.5',
    date: '2024-12-09',
    title: 'UI Improvements',
    changes: [
      'Styling improvements',
      'Implemented internal versioning',
      'Enhanced visual consistency',
    ],
  },
  {
    version: '1.1.4',
    date: '2024-12-09',
    title: 'Navigation Updates',
    changes: [
      'Fixed navigation highlighting',
      'Changed "Update status" button text',
      'Improved user interface feedback',
    ],
  },
  {
    version: '1.1.3',
    date: '2024-12-06',
    title: 'Role Management',
    changes: [
      'Added instant role updating',
      'Mobile menu improvements',
      'Enhanced user experience',
    ],
  },
  {
    version: '1.1.2',
    date: '2024-12-05',
    title: 'User Management Enhancement',
    changes: [
      'Improved user management UI',
      'Added master key for account creation',
      'Enhanced role management',
    ],
  },
  {
    version: '1.1.1',
    date: '2024-12-05',
    title: 'Initial Updates',
    changes: [
      'Updated documentation',
      'Moved user management button on mobile',
      'Added mobile friendly interface',
    ],
  },
  {
    version: '1.1.0',
    date: '2024-11-25',
    title: 'Project Setup',
    changes: [
      'Created bare bones frontend and backend',
      'Added front-end authentication',
      'Initial project setup',
    ],
  },
  {
    version: '1.0.0',
    date: '2024-11-25',
    title: 'Initial Release',
    changes: ['Initial commit', 'Project repository initialization', 'Basic project structure'],
  },
];

export function UpdateLog() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  useEffect(() => {
    const lastSeenVersion = localStorage.getItem('lastSeenVersion');
    if (lastSeenVersion !== APP_VERSION) {
      setShowBadge(true);
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    setShowBadge(false);
    localStorage.setItem('lastSeenVersion', APP_VERSION);
  };

  return (
    <>
      <Button variant='ghost' size='sm' onClick={handleOpen} className='relative hover:bg-accent'>
        v{APP_VERSION}
        {showBadge && (
          <span className='absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse' />
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='max-w-3xl'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold'>Update Timeline</DialogTitle>
          </DialogHeader>
          <ScrollArea className='max-h-[600px] pr-4'>
            <div className='relative space-y-0 ml-4 mt-8'>
              {/* Timeline line */}
              <div className='absolute left-0 top-0 bottom-0 w-px bg-border' />

              {updates.map((update, index) => (
                <div key={update.version} className='relative pb-10 last:pb-0'>
                  {/* Timeline dot */}
                  <div
                    className={`absolute -left-[13px] w-6 h-6 rounded-full border-4 ${
                      index === 0 ? 'bg-primary border-primary/20' : 'bg-background border-border'
                    }`}
                  />

                  {/* Content card */}
                  <div
                    className={`ml-8 p-4 rounded-lg transition-colors ${
                      index === 0 ? 'bg-accent/30 border border-accent' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className='flex flex-col space-y-1 mb-2'>
                      <div className='flex items-center justify-between'>
                        <h3 className='font-semibold text-lg flex items-center gap-2'>
                          {update.title}
                          {index === 0 && (
                            <Badge variant='default' className='bg-primary'>
                              Latest
                            </Badge>
                          )}
                        </h3>
                        <div className='text-sm text-muted-foreground'>
                          <span className={`font-medium ${index === 0 ? 'text-primary' : ''}`}>
                            v{update.version}
                          </span>
                          <span className='mx-2'>•</span>
                          <span>{update.date}</span>
                        </div>
                      </div>
                    </div>
                    <ul className='list-disc list-inside space-y-1.5 ml-1'>
                      {update.changes.map((change, i) => (
                        <li
                          key={i}
                          className={`text-muted-foreground ${
                            index === 0 ? 'text-foreground' : ''
                          }`}
                        >
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
